// ANGEL FLIGHT — IntroCinematic: the "LAST BIRD UP" opening cue list.
//
// Presentation-only. Runs while GameLoop.introHold keeps the sim frozen (no
// enemies, no input, no economy) but the world keeps scrolling/banking under
// the rAF loop. Owns a DOM overlay (blackout + typed radio/Reyes/self-test
// copy + title card), drives the FLIR boot blinks through view.setMode, and
// raises the gun viewmodel via GunView3D.riseOffset. Skippable at any moment
// (any key / tap) — skip() restores the exact same state finish() does, so
// skip and full-play hand off identically.
//
// All timings/copy live in config/cinematic.ts. No magic numbers here.

import { CINEMATIC as C } from '../config/cinematic';

/** The slice of GameView3D the cinematic drives (kept narrow for tests). */
export interface CinematicView {
  setMode(mode: 'color' | 'thermal'): void;
  getMode(): 'color' | 'thermal';
}

/** The slice of GunView3D the cinematic drives. */
export interface CinematicGun {
  riseOffset: number;
}

export interface IntroCinematicOpts {
  root: HTMLElement;
  view: CinematicView;
  gun: CinematicGun;
  /** Live gunner ref — the spin pulse writes spinup (sim is held, so this is
   *  render-only state until hand-off; finish() zeroes it). */
  gunner: { spinup: number };
  onDone: () => void;
}

/** One typed-text cue: target element, full string, start time. */
interface TypeCue {
  el: HTMLElement;
  text: string;
  startS: number;
  shown: number; // chars currently rendered (diffed — only writes on change)
}

export class IntroCinematic {
  private readonly opts: IntroCinematicOpts;
  private readonly overlay: HTMLDivElement;
  private readonly blackout: HTMLDivElement;
  private readonly selftest: HTMLDivElement;
  private readonly titleWrap: HTMLDivElement;
  private readonly barTop: HTMLDivElement;
  private readonly barBottom: HTMLDivElement;
  private readonly cues: TypeCue[] = [];
  private t = 0;
  private thermalApplied = false;
  private barsIn = false;
  private barsOut = false;
  private done = false;

  constructor(opts: IntroCinematicOpts) {
    this.opts = opts;

    this.overlay = document.createElement('div');
    Object.assign(this.overlay.style, {
      position: 'absolute',
      inset: '0',
      pointerEvents: 'auto',
      zIndex: '40',
      font: "600 15px/1.5 ui-monospace, 'SFMono-Regular', Menlo, monospace",
      letterSpacing: '0.1em',
      color: '#cfe6cf',
      userSelect: 'none',
      cursor: 'pointer',
    } as Partial<CSSStyleDeclaration>);

    // Full-screen blackout for the radio phase; fades to reveal the live world.
    this.blackout = document.createElement('div');
    Object.assign(this.blackout.style, {
      position: 'absolute',
      inset: '0',
      background: '#000',
      opacity: '1',
      transition: `opacity ${C.revealFadeS}s ease-out`,
    } as Partial<CSSStyleDeclaration>);
    this.overlay.appendChild(this.blackout);

    // Cinematic letterbox: top/bottom film bars. Created flat (height 0); they
    // ease in when the world is revealed and retract on hand-off (see update).
    // Behind the typed copy (appended next) but above the live world.
    this.barTop = document.createElement('div');
    this.barBottom = document.createElement('div');
    const barCss: Partial<CSSStyleDeclaration> = {
      position: 'absolute',
      left: '0',
      right: '0',
      height: '0',
      background: '#000',
      pointerEvents: 'none',
      transition: `height ${C.letterboxInS}s ease-out`,
    };
    Object.assign(this.barTop.style, barCss, { top: '0' } as Partial<CSSStyleDeclaration>);
    Object.assign(this.barBottom.style, barCss, { bottom: '0' } as Partial<CSSStyleDeclaration>);
    this.overlay.append(this.barTop, this.barBottom);

    // Radio traffic (typed, centered-left block).
    const radioWrap = document.createElement('div');
    Object.assign(radioWrap.style, {
      position: 'absolute',
      left: '12%',
      top: '38%',
      maxWidth: '70%',
      textShadow: '0 0 6px rgba(125,255,154,0.4)',
      color: '#7dff9a',
    } as Partial<CSSStyleDeclaration>);
    this.blackout.appendChild(radioWrap);
    for (let i = 0; i < C.radioLines.length; i++) {
      const line = document.createElement('div');
      line.style.minHeight = '1.5em';
      radioWrap.appendChild(line);
      this.cues.push({ el: line, text: C.radioLines[i] ?? '', startS: C.radioStartS[i] ?? 0, shown: 0 });
    }

    // Reyes lines (lower-left, in-mission comms styling).
    const reyes = document.createElement('div');
    Object.assign(reyes.style, {
      position: 'absolute',
      left: '4%',
      bottom: '16%',
      maxWidth: '60%',
      color: '#ffd27a',
      textShadow: '0 1px 3px rgba(0,0,0,0.8)',
    } as Partial<CSSStyleDeclaration>);
    this.overlay.appendChild(reyes);
    const reyes1 = document.createElement('div');
    const reyes2 = document.createElement('div');
    const hint = document.createElement('div');
    Object.assign(hint.style, { color: '#cfd6e0', marginTop: '0.4em', fontSize: '12px' });
    reyes.append(reyes1, reyes2, hint);
    this.cues.push({ el: reyes1, text: C.reyes1, startS: C.reyes1S, shown: 0 });
    this.cues.push({ el: reyes2, text: C.reyes2, startS: C.reyes2S, shown: 0 });
    this.cues.push({ el: hint, text: C.controlHint, startS: C.reyes2S + 1.2, shown: 0 });

    // FLIR self-test readout (top-center terminal block; shown during blinks).
    this.selftest = document.createElement('div');
    Object.assign(this.selftest.style, {
      position: 'absolute',
      left: '50%',
      top: '18%',
      transform: 'translateX(-50%)',
      textAlign: 'center',
      color: '#e8fbe8',
      textShadow: '0 0 8px rgba(232,251,232,0.6)',
      whiteSpace: 'pre',
      display: 'none',
    } as Partial<CSSStyleDeclaration>);
    this.overlay.appendChild(this.selftest);

    // Title card.
    this.titleWrap = document.createElement('div');
    Object.assign(this.titleWrap.style, {
      position: 'absolute',
      left: '50%',
      top: '40%',
      transform: 'translate(-50%, -50%)',
      textAlign: 'center',
      opacity: '0',
      transition: `opacity 0.6s ease-in-out`,
    } as Partial<CSSStyleDeclaration>);
    const titleEl = document.createElement('div');
    titleEl.textContent = C.title;
    Object.assign(titleEl.style, {
      fontSize: '44px',
      letterSpacing: '0.35em',
      color: '#ffd27a',
      textShadow: '0 0 18px rgba(255,172,99,0.55)',
    } as Partial<CSSStyleDeclaration>);
    const subEl = document.createElement('div');
    subEl.textContent = C.subtitle;
    Object.assign(subEl.style, {
      marginTop: '0.6em',
      fontSize: '15px',
      letterSpacing: '0.3em',
      color: '#cfd6e0',
    } as Partial<CSSStyleDeclaration>);
    const reyes3 = document.createElement('div');
    Object.assign(reyes3.style, {
      marginTop: '1.6em',
      fontSize: '14px',
      color: '#ffd27a',
    } as Partial<CSSStyleDeclaration>);
    this.titleWrap.append(titleEl, subEl, reyes3);
    this.overlay.appendChild(this.titleWrap);
    this.cues.push({ el: reyes3, text: C.reyes3, startS: C.titleS + 0.5, shown: 0 });

    // Skip hint (always visible).
    const skip = document.createElement('div');
    skip.textContent = 'TAP / ANY KEY — SKIP';
    Object.assign(skip.style, {
      position: 'absolute',
      right: '3%',
      bottom: '4%',
      fontSize: '11px',
      color: 'rgba(207,214,224,0.55)',
    } as Partial<CSSStyleDeclaration>);
    this.overlay.appendChild(skip);

    this.overlay.addEventListener('pointerdown', this.onPointer);
    opts.root.appendChild(this.overlay);

    // Gun starts lowered out of frame.
    opts.gun.riseOffset = C.gunDropY;
  }

  get isDone(): boolean {
    return this.done;
  }

  private readonly onPointer = (e: PointerEvent): void => {
    e.stopPropagation();
    this.skip();
  };

  /** Advance the cue list. `dtMs` milliseconds; safe to stop calling after done. */
  update(dtMs: number): void {
    if (this.done) return;
    this.t += dtMs / 1000;
    const t = this.t;

    // Typewriter cues (diffed writes only).
    for (const cue of this.cues) {
      const want =
        t <= cue.startS
          ? 0
          : Math.min(cue.text.length, Math.floor((t - cue.startS) * C.charsPerSec));
      if (want !== cue.shown) {
        cue.shown = want;
        cue.el.textContent = cue.text.slice(0, want);
      }
    }

    // Blackout reveal.
    if (t >= C.revealS && this.blackout.style.opacity !== '0') {
      this.blackout.style.opacity = '0';
      this.blackout.style.pointerEvents = 'none';
    }

    // Letterbox: ease the film bars in as the world appears, then retract them
    // on the hand-off so the frame opens up into gameplay.
    if (!this.barsIn && t >= C.revealS) {
      this.barsIn = true;
      const h = `${(C.letterboxFrac * 100).toFixed(2)}vh`;
      this.barTop.style.height = h;
      this.barBottom.style.height = h;
    }
    if (!this.barsOut && t >= C.endS) {
      this.barsOut = true;
      this.barTop.style.transition = `height ${C.endFadeS}s ease-in`;
      this.barBottom.style.transition = `height ${C.endFadeS}s ease-in`;
      this.barTop.style.height = '0';
      this.barBottom.style.height = '0';
    }

    // FLIR windows: hidden precompile round-trip + the two visible boot blinks.
    const inWin = (start: number, len: number): boolean => t >= start && t < start + len;
    const wantThermal =
      inWin(C.precompileThermalOnS, C.precompileThermalOffS - C.precompileThermalOnS) ||
      inWin(C.blink1S, C.blinkLenS) ||
      inWin(C.blink2S, C.blinkLenS);
    if (wantThermal !== this.thermalApplied) {
      this.thermalApplied = wantThermal;
      this.opts.view.setMode(wantThermal ? 'thermal' : 'color');
    }

    // Self-test readout text (block swaps, not typed — terminal style).
    if (t >= C.blink1S && t < C.blink2S) {
      this.setSelftest(C.selftest1);
    } else if (t >= C.blink2S && t < C.selftestClearS) {
      this.setSelftest(C.selftest2);
    } else if (this.selftest.style.display !== 'none') {
      this.selftest.style.display = 'none';
    }

    // Gun rise (eased) + one spin pulse.
    if (t >= C.gunRiseS) {
      const k = Math.min(1, (t - C.gunRiseS) / C.gunRiseLenS);
      const eased = 1 - (1 - k) * (1 - k) * (1 - k); // ease-out cubic
      this.opts.gun.riseOffset = C.gunDropY * (1 - eased);
    }
    if (t >= C.spinPulseS && t < C.spinPulseS + C.spinPulseLenS) {
      // Triangle pulse 0→1→0 across the window.
      const k = (t - C.spinPulseS) / C.spinPulseLenS;
      this.opts.gunner.spinup = k < 0.5 ? k * 2 : (1 - k) * 2;
    } else if (t >= C.spinPulseS + C.spinPulseLenS) {
      this.opts.gunner.spinup = 0;
    }

    // Title card in/out.
    if (t >= C.titleS && t < C.endS && this.titleWrap.style.opacity !== '1') {
      this.titleWrap.style.opacity = '1';
    }
    if (t >= C.endS && this.titleWrap.style.opacity !== '0') {
      this.titleWrap.style.opacity = '0';
    }

    if (t >= C.endS + C.endFadeS) this.finish();
  }

  private setSelftest(lines: readonly string[]): void {
    const text = lines.join('\n');
    if (this.selftest.textContent !== text) this.selftest.textContent = text;
    if (this.selftest.style.display !== 'block') this.selftest.style.display = 'block';
  }

  /** Instant skip — restores the exact finish() state. */
  skip(): void {
    this.finish();
  }

  private finish(): void {
    if (this.done) return;
    this.done = true;
    // Restore the colour pipeline (the FLIR toggle stays the player's call).
    if (this.opts.view.getMode() !== 'color') this.opts.view.setMode('color');
    this.opts.gun.riseOffset = 0;
    this.opts.gunner.spinup = 0;
    this.overlay.removeEventListener('pointerdown', this.onPointer);
    this.overlay.remove();
    this.opts.onDone();
  }

  /** Teardown without firing onDone (mission abort while the intro runs). */
  destroy(): void {
    if (this.done) return;
    this.done = true;
    if (this.opts.view.getMode() !== 'color') this.opts.view.setMode('color');
    this.opts.gun.riseOffset = 0;
    this.opts.gunner.spinup = 0;
    this.overlay.removeEventListener('pointerdown', this.onPointer);
    this.overlay.remove();
  }
}
